

import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../api/axiosInstance';
import { getToken, setToken } from '../../utils/secureStorage';
import { FiEye, FiEyeOff, FiHome } from 'react-icons/fi';

const Login = () => {
  const [email, setemail] = useState('');
  const [proceeding, setProceeding] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Helper to get query param
  const getQueryParam = (name) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  };

  useEffect(() => {
    const refreshToken = getToken('refreshToken');
    if (refreshToken) {
      const redirectLink = getQueryParam('l');
      navigate(redirectLink ? decodeURIComponent(redirectLink) : '/dashboard');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setProceeding(true)
    setError('');
    try {
      const response = await api.post('/login', { email, password });
      if (response.data.error) {
        setProceeding(false);
        setError(response.data.message);
      } else {
        setToken('email', email);
        setToken('accessToken', response.data.accessToken);
        setToken('refreshToken', response.data.refreshToken);
        setToken('roles', response.data.roles);

        const redirectLink = getQueryParam('l');
        navigate(redirectLink ? decodeURIComponent(redirectLink) : '/dashboard');
      }
    } catch (err) {
      setProceeding(false);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials and try again.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-gray-50 dark:from-gray-900 dark:to-gray-800 px-4 py-12">
      <div className="w-full max-w-md">
        {/* Skip to Home Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-orange-600 dark:text-gray-400 dark:hover:text-orange-400 transition-colors mb-6"
        >
          <FiHome size={16} />
          <span>Back to Dashboard</span>
        </Link>

        {/* Login Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-8 pb-6 border-b border-gray-100 dark:border-gray-700">
            <div className="flex justify-center mb-6">
              <img src="/images/lpu.svg" className="h-20" alt="LPU Logo" />
            </div>
            <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white">
              Welcome Back
            </h2>
            <p className="text-center text-gray-600 dark:text-gray-400 mt-2 text-sm">
              Sign in to access Samarthya
            </p>
          </div>

          {/* Form */}
          <div className="p-8">
            <form onSubmit={handleLogin} className="space-y-6">
              {error && (
                <div className="flex items-start gap-3 p-4 text-sm text-red-800 bg-red-50 rounded-xl border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800" role="alert">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setemail(e.target.value)}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 block w-full p-3 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-orange-500 dark:focus:border-orange-500"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 block w-full p-3 pr-12 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-orange-500 dark:focus:border-orange-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                    tabIndex="-1"
                  >
                    {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={proceeding}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 text-base font-medium text-white bg-orange-600 rounded-xl hover:bg-orange-700 focus:ring-4 focus:ring-orange-300 transition-all disabled:opacity-70 disabled:cursor-not-allowed dark:bg-orange-600 dark:hover:bg-orange-700 dark:focus:ring-orange-800"
              >
                {proceeding ? (
                  <>
                    <svg aria-hidden="true" className="w-5 h-5 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" opacity="0.25" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
                    </svg>
                    <span>Signing In...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Footer Text */}
        <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-6">
            Samarthya - Productivity Suite by
            <a href="https://webhizzy.in" target="_blank" rel="noreferrer" className="text-orange-600 hover:text-orange-700 dark:text-orange-500 dark:hover:text-orange-400 font-medium">
            <svg viewBox="11.32 132.11 187.37 32.78" xmlns="http://www.w3.org/2000/svg" height="12" className="inline mx-1">
                <g transform="matrix(0.41864164, 0, 0, 0.41864164, -109.34452, -12.25839)">
                    <g id="logo-center">
                        <g id="title">
                            <path id="path139560" d="m 362.79687,-43.344 22.104,43.416 5.904,-11.808 -16.056,-31.608 z m 21.60001,0.072 21.95999,43.344 5.976,-11.808 -15.98399,-31.536 z m 32.976,0.072 c -3.67201,0 -6.69601,3.024 -6.69601,6.696 0,3.672 3.024,6.696 6.69601,6.696 3.74399,0 6.76799,-3.024 6.76799,-6.696 0,-3.672 -3.024,-6.696 -6.76799,-6.696 z" strokeWidth="0" strokeLinejoin="miter" strokeMiterlimit="2" fill="#1a005a" stroke="#1a005a" transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"></path>
                            <path id="path139562" d="m 436.778,-20.088 c -0.792,-5.472 1.008,-10.656 4.536,-14.4 -9.36,1.512 -15.768,10.296 -14.4,19.656 1.368,9.504 10.224,16.056 19.656,14.688 4.104,-0.576 7.632,-2.52 10.224,-5.328 -0.144,0 -0.216,0.072 -0.36,0.072 -9.504,1.368 -18.288,-5.184 -19.656,-14.688 z m 11.736,-2.736 -8.064,6.336 h 16.056 c 4.464,-11.592 -4.608,-18 -12.24,-18 5.616,5.4 4.248,11.664 4.248,11.664 z" strokeWidth="0" strokeLinejoin="miter" strokeMiterlimit="2" fill="#1f1169" stroke="#1f1169" transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"></path>
                            <path id="path139564" d="m 497.402,-22.968 c -3.168,-8.856 -12.816,-13.536 -21.672,-10.584 4.824,1.656 8.784,5.4 10.656,10.512 3.168,8.928 -1.512,18.792 -10.512,21.96 -0.072,0 -0.216,0.072 -0.288,0.072 3.528,1.224 7.56,1.296 11.376,0 8.928,-3.168 13.608,-13.032 10.44,-21.96 z M 463.85,-0.432 c 0.864,0.288 1.728,0.432 2.736,0.432 4.32,0 7.848,-3.528 7.848,-7.848 V -43.272 H 463.85 Z" stroke Width="0" strokeLinejoin="miter" strokeMiterlimit="2" fill="#232278" stroke="#232278" transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"></path>
                            <path id="path139566" d="m 515.21525,0 v -43.272 h -10.584 V 0 Z m 12.168,-34.488 c -1.728,0 -3.456,0.576 -4.824,1.728 l -5.4,4.176 h 7.488 V 0 h 10.656 v -26.568 c 0,-4.392 -3.528,-7.92 -7.92,-7.92 z" strokeWidth="0" strokeLinejoin="miter" strokeMiterlimit="2" fill="#283387" stroke="#283387" transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"></path>
                            <path id="path139568" d="M 553.184,-28.584 H 542.6 V -7.92 c 0,4.392 3.528,7.92 7.848,7.92 1.8,0 3.528,-0.576 4.896,-1.728 l 5.184,-4.032 h -7.344 z m -5.256,-3.96 c 2.952,0 5.328,-2.376 5.328,-5.328 0,-2.952 -2.376,-5.4 -5.328,-5.4 -2.952,0 -5.328,2.448 -5.328,5.4 0,2.952 2.376,5.328 5.328,5.328 z" strokeWidth="0" strokeLinejoin="miter" strokeMiterlimit="2" fill="#2c4497" stroke="#2c4497" transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"></path>
                            <path id="path139570" d="m 580.60025,-34.488 -17.424,34.56 h 11.88 l 17.496,-34.56 z zm 11.952,34.56 v -10.296 l -12.312,10.296 z m -29.376,-34.56 v 10.224 l 12.24,-10.224 z" strokeWidth="0" strokeLinejoin="miter" strokeMiterlimit="2" fill="#3155a6" stroke="#3155a6" transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"></path>
                            <path id="path139572" d="m 615.05337,-34.488 -17.424,34.56 h 11.88 l 17.496,-34.56 z zm 11.952,34.56 V -10.224 L 614.69338,0.072 Z zm -29.376,-34.56 v 10.224 l 12.24,-10.224 z" strokeWidth="0" strokeLinejoin="miter" strokeMiterlimit="2" fill="#3566b5" stroke="#3566b5" transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"></path>
                            <path id="path139574" d="m 639.8585,0 c -2.448,0 -4.392,2.016 -4.392,4.464 0,2.376 1.944,4.392 4.392,4.392 2.448,0 4.392,-2.016 4.392,-4.392 0,-2.448 -1.944,-4.464 -4.392,-4.464 z m 15.912,-34.488 c -3.024,0 -5.4,2.376 -5.4,5.4 0,2.952 2.376,5.4 5.4,5.4 2.952,0 5.4,-2.448 5.4,-5.4 0,-3.024 -2.448,-5.4 -5.4,-5.4 z m -26.208,0 17.424,34.56 5.976,-11.808 -11.448,-22.752 z" strokeWidth="0" strokeLinejoin="miter" strokeMiterlimit="2" fill="#3a77c4" stroke="#3a77c4" transform="matrix(1.5,0,0,1.5,-255.97553,409.866)"></path>
                        </                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                          g>
                    </g>
                </g>
            </svg>
            </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
